import React, { useEffect, useMemo, useState } from 'react';

import { ConfettiCircleSVG, ConfettiSquareSVG, ConfettiStarSVG, ConfettiTriangleSVG } from '@components/svgs';

interface ConfettiBackgroundSVGProps {
  count?: number;
  className?: string;
  style?: React.CSSProperties;
}

interface ConfettiElement {
  key: string;
  element: JSX.Element;
}

interface ConfettiPosition {
  leftPercent: number;
  topPercent: number;
  rotation: number;
  opacity: number;
}

const COLORS = [
  '#facc15', // yellow
  '#93c5fd', // blue
  '#f9a8d4', // pink
  '#86efac', // green
  '#c4b5fd', // purple
  '#fdba74' // orange
];

const SHAPE_TYPES = {
  CIRCLE: 0,
  TRIANGLE: 1,
  SQUARE: 2,
  STAR: 3,
  STRIPE: 4
};

const generateRandomPosition = (viewportHeight: number): ConfettiPosition => ({
  leftPercent: Math.random() * 100,
  topPercent: Math.random() * viewportHeight,
  rotation: Math.random() * 360,
  opacity: 0.4 + Math.random() * 0.4
});

const getConfettiSize = (index: number): string => {
  return index % 3 === 0 ? 'h-4 w-4' : 'h-3 w-3';
};

const getConfettiStyle = (position: ConfettiPosition) => ({
  left: `${position.leftPercent}%`,
  top: `${position.topPercent}px`,
  transform: `rotate(${position.rotation}deg)`,
  opacity: position.opacity
});

const generateSVGConfetti = (
  index: number,
  color: string,
  position: ConfettiPosition,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: React.ComponentType<any>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: any = {}
): ConfettiElement => {
  const size = getConfettiSize(index);
  const style = getConfettiStyle(position);

  return {
    key: `confetti-${Component.name.toLowerCase()}-${index}`,
    element: <Component color={color} size={size} className='absolute opacity-60' style={style} {...props} />
  };
};

const generateStripeConfetti = (index: number, color: string, position: ConfettiPosition): ConfettiElement => {
  const stripeLength = index % 2 === 0 ? 'w-7 h-0.5' : 'w-5 h-0.5';
  const style = getConfettiStyle(position);

  return {
    key: `confetti-stripe-${index}`,
    element: (
      <div
        className={`absolute ${stripeLength} opacity-60`}
        style={{
          ...style,
          backgroundColor: color
        }}
      />
    )
  };
};

const generateConfettiElement = (index: number, viewportHeight: number): ConfettiElement => {
  const color = COLORS[index % COLORS.length];
  const shapeType = index % Object.keys(SHAPE_TYPES).length;
  const position = generateRandomPosition(viewportHeight);

  switch (shapeType) {
    case SHAPE_TYPES.CIRCLE:
      return generateSVGConfetti(index, color, position, ConfettiCircleSVG);
    case SHAPE_TYPES.TRIANGLE:
      return generateSVGConfetti(index, color, position, ConfettiTriangleSVG);
    case SHAPE_TYPES.SQUARE:
      return generateSVGConfetti(index, color, position, ConfettiSquareSVG);
    case SHAPE_TYPES.STAR:
      return generateSVGConfetti(index, color, position, ConfettiStarSVG);
    case SHAPE_TYPES.STRIPE:
    default:
      return generateStripeConfetti(index, color, position);
  }
};

const generateConfettiElements = (count: number, viewportHeight: number): JSX.Element[] => {
  const confettiElements: ConfettiElement[] = [];

  for (let i = 0; i < count; i++) {
    confettiElements.push(generateConfettiElement(i, viewportHeight));
  }

  return confettiElements.map(({ key, element }) => React.cloneElement(element, { key }));
};

export const ConfettiBackgroundSVG: React.FC<ConfettiBackgroundSVGProps> = ({
  count = 100,
  className = '',
  style = {}
}) => {
  // Get viewport height dynamically
  const getInitialHeight = () => (typeof window !== 'undefined' ? window.innerHeight : 800);

  const [viewportHeight, setViewportHeight] = useState(getInitialHeight);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const updateHeight = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => setViewportHeight(window.innerHeight), 100);
    };

    window.addEventListener('resize', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
      clearTimeout(timeoutId);
    };
  }, []);

  const confettiElements = useMemo(() => generateConfettiElements(count, viewportHeight), [count, viewportHeight]);

  return (
    <div
      className={`pointer-events-none fixed ${className}`}
      style={{
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        ...style
      }}
    >
      {confettiElements}
    </div>
  );
};
